import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input, Modal, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Eye, Shield, AlertTriangle, User, Image } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

export default function ViewLicenseRenewal() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");

    // Modal State
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('1');

    const prevSearch = useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return; // Exit early, let page change trigger next fetch
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search = "", sortCol = "", sortDir = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };
            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("license_renewal/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total || 0);
            } else {
                setData(response);
                setTotalRows(response.length || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_license_renewal`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_license_renewal`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("license_renewal/delete", { id: id });
                    toast.success("Record deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete record", { autoClose: 1000 });
                }
            }
        });
    };

    // Modal Helpers
    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550);
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }
    const handleView = (row: any) => {
        setSelectedLicense(row);
        setActiveTab('1');
        setViewModal(true);
    };

    // Calculate Status for Modal Header
    const getStatusDetails = (expiryDate: string) => {
        if (!expiryDate) return { color: '#6c757d', bg: 'rgba(108, 117, 125, 0.2)', text: 'N/A' };

        const expiry = new Date(expiryDate);
        const today = new Date();
        const diffTime = expiry.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.2)', text: 'Expired' };
        if (diffDays <= 30) return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.2)', text: 'Expiring Soon' };
        return { color: '#10b981', bg: 'rgba(16, 185, 129, 0.2)', text: 'Active' };
    };

    const columns = [
        {
            name: <div>Sl. No</div>,
            selector: (row: any, index: number | undefined) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },

        {
            name: <div>Driver Name</div>,
            selector: (row: any) => row.driver?.name || 'N/A',
            sortable: true,
            sortField: "driver_name",
            wrap: true,
            cell: (row: any) => (
                <span className="text-primary fw-bold">
                    {row.driver?.name || 'N/A'}
                    {(row.driver?.deleted_at || row.deleted_at) && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            )
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch?.branch_name || 'N/A',
            sortable: true,
            sortField: "branch_name",
            wrap: true,
            cell: (row: any) => (
                <span>
                    {row.branch?.branch_name || 'N/A'}
                    {row.branch?.deleted_at && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            )
        },
        {
            name: <div>License Number</div>,
            selector: (row: any) => row.license_number,
            sortable: true,
            sortField: "license_number",
            wrap: true
        },
        {
            name: <div>Expiry Date</div>,
            selector: (row: any) => row.expiry_date,
            sortable: true,
            sortField: "expiry_date",
            wrap: true,
            cell: (row: any) => row.expiry_date ? new Date(row.expiry_date).toLocaleDateString('en-GB') : "N/A"
        },
        {
            name: <div>Actions</div>,
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('license-renewal.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('license-renewal.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('license-renewal.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="License Renewals" parent="Renewal" title="License Renewals" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">License Renewals Overview</H3>
                                {hasPermission('license-renewal.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Renewal
                                    </Btn>
                                )}
                            </Col>
                            <>
                                <Col xs={12} className="mb-3 d-flex justify-content-end">
                                    <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                </Col>
                                <div className="table-responsive">
                                    <DataTable
                                        data={data}
                                        columns={columns}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onSort={handleSort}
                                        sortServer
                                        progressPending={loading}
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        highlightOnHover
                                        customStyles={customStyles}
                                    />
                                </div>
                            </>
                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View License Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                {selectedLicense && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                        padding: '1.5rem',
                        position: 'relative', width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={handleClose}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        ></button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <User size={30} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                }}>
                                    {selectedLicense.driver?.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>License: {selectedLicense.license_number}</span>
                                    <span>•</span>
                                    <span>{selectedLicense.branch?.branch_name}</span>
                                </div>
                            </div>
                            <div>
                                {(() => {
                                    const status = getStatusDetails(selectedLicense.expiry_date);
                                    return (
                                        <span style={{
                                            backgroundColor: status.bg,
                                            color: status.color,
                                            padding: '0.5rem 1rem',
                                            margin: '0.5rem 1rem',
                                            borderRadius: '20px',
                                            fontSize: '0.875rem',
                                            fontWeight: 700,
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            backdropFilter: 'blur(4px)'
                                        }}>
                                            {status.text}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Shield size={16} /> Overview
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Image size={16} /> License Images
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedLicense && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Branch</label>
                                                    <div className="fw-semibold">{selectedLicense.branch?.branch_name || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Driver</label>
                                                    <div className="fw-semibold text-primary">
                                                        {selectedLicense.driver?.name}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">License Number</label>
                                                    <div className="fw-semibold">{selectedLicense.license_number}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Star Date</label>
                                                    <div className="fw-semibold">{selectedLicense.start_date || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Expiry Date</label>
                                                    <div className="fw-bold fs-6">
                                                        {selectedLicense.expiry_date || 'N/A'}
                                                        {getStatusDetails(selectedLicense.expiry_date).text === 'Expired' && <AlertTriangle size={14} className="text-danger ms-2" />}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Remarks</label>
                                                    <div className="p-2 bg-light rounded text-dark small" style={{ minHeight: '60px' }}>
                                                        {selectedLicense.remarks || <em className="text-muted">No remarks.</em>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase mb-2">Front Image</label>
                                                <div className="border rounded p-2 text-center" style={{ minHeight: '200px', backgroundColor: '#f1f1f1' }}>
                                                    {selectedLicense.image_front ? (
                                                        <img
                                                            src={`${mediaUrl}${selectedLicense.image_front}`}
                                                            alt="License Front"
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '200px' }}
                                                        />
                                                    ) : (
                                                        <div className="text-muted mt-5 pt-4">No Front Image</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase mb-2">Back Image</label>
                                                <div className="border rounded p-2 text-center" style={{ minHeight: '200px', backgroundColor: '#f1f1f1' }}>
                                                    {selectedLicense.image_back ? (
                                                        <img
                                                            src={`${mediaUrl}${selectedLicense.image_back}`}
                                                            alt="License Back"
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '200px' }}
                                                        />
                                                    ) : (
                                                        <div className="text-muted mt-5 pt-4">No Back Image</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}
